import React, { useCallback, useEffect, useRef, useState } from "react";
import "../../../../../../components/common/EditableCellGrid/EditableCellGrid.css";

const FlightNumberCellEditor = ({ row, column, onRowChange, onClose }) => {
    const originalValue = row[column.key] || "";
    const [value, setValue] = useState(originalValue);
    const [suggestion, setSuggestion] = useState("");
    const inputRef = useRef(null);
    const closedRef = useRef(false);
    const suggestionAppliedRef = useRef(false);

    const applyValue = useCallback((nextValue, commit = false) => {
        setValue(nextValue);
        onRowChange({ ...row, [column.key]: nextValue }, commit);
    }, [row, column.key, onRowChange]);

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    useEffect(() => {
        let active = true;
        const cachedSuggestion = column.getFlightNumberSuggestion?.(row.aircraftReg, row.departureAirport, row.arrivalAirport) || "";
        if (cachedSuggestion && !suggestionAppliedRef.current) {
            suggestionAppliedRef.current = true;
            setSuggestion(cachedSuggestion);
            applyValue(cachedSuggestion);
            setTimeout(() => {
                inputRef.current?.select();
            }, 0);
        } else {
            setSuggestion(cachedSuggestion);
            setValue(originalValue);
        }

        column.loadFlightNumberSuggestion?.(row.aircraftReg, row.departureAirport, row.arrivalAirport).then((nextSuggestion) => {
            if (!active) {
                return;
            }

            const availableSuggestion = nextSuggestion || "";
            setSuggestion(availableSuggestion);
            if (availableSuggestion && !suggestionAppliedRef.current) {
                suggestionAppliedRef.current = true;
                applyValue(availableSuggestion);
                setTimeout(() => {
                    inputRef.current?.select();
                }, 0);
            }
        });

        return () => {
            active = false;
        };
    }, [column, originalValue, row.aircraftReg, row.arrivalAirport, row.departureAirport, applyValue]);

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            if (closedRef.current) {
                return;
            }
            closedRef.current = true;
            onRowChange({ ...row, [column.key]: value }, true);
            onClose(true, false);
        } else if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            if (closedRef.current) {
                return;
            }
            closedRef.current = true;
            onClose(false, false);
        }
    };

    const commitOnBlur = () => {
        if (closedRef.current) {
            return;
        }
        closedRef.current = true;
        const finalValue = value.trim() || originalValue;
        if (finalValue !== originalValue || value !== finalValue) {
            onRowChange({ ...row, [column.key]: finalValue }, true);
        }
        onClose(true, false);
    };

    return (
        <div
            className="editable-cell-editor schedule-draft-rdg-flight-number-editor"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            <input
                ref={inputRef}
                className="editable-cell-editor schedule-draft-rdg-flight-number-input"
                value={value}
                onChange={(event) => applyValue(event.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={commitOnBlur}
            />
            {suggestion && suggestion !== value && (
                <button
                    type="button"
                    tabIndex={-1}
                    className="schedule-draft-rdg-flight-number-suggestion"
                    aria-label={`使用建议航班号 ${suggestion}`}
                    onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                        applyValue(suggestion);
                        setTimeout(() => {
                            inputRef.current?.focus();
                            inputRef.current?.select();
                        }, 0);
                    }}
                >
                    <span className="schedule-draft-rdg-flight-number-suggestion-label">建议:</span>
                    <strong>{suggestion}</strong>
                    <span>— 该飞机最近飞此航段使用</span>
                </button>
            )}
        </div>
    );
};

export default FlightNumberCellEditor;
